import React, { useMemo } from "react";
import * as Yup from "yup";
import { Formik, FieldArray, Form, ErrorMessage } from "formik";
import { v4 as uuidv4 } from "uuid";
import Button from "../component-lib/Molecules/button/Button";
import Heading from "../component-lib/Atoms/heading/EventEHRHeading";
import Input from "../component-lib/Atoms/Input/Input";
import { deleteIcon } from "../assets";
import { CUSTOM_lABEL_STYLE } from "../screens/patient-screen/visit-flow/constant";
import { TEXT_CONSTANTS } from "../utils/text-constants/textConstants";

interface GenericOrderFormProps {
  isItemRequired: boolean;
  isNoteRequired: boolean;
  getFormData: (data: any) => void;
}

export default function GenericOrderForm({
  isItemRequired,
  isNoteRequired,
  getFormData,
}: GenericOrderFormProps) {
  const validationSchema = useMemo(() => {
    const shape: Record<string, any> = {};
    if (isItemRequired) {
      shape.items = Yup.array()
        .of(
          Yup.object().shape({
            item: Yup.string().required("Item is required"),
            id: Yup.string().required(),
          })
        )
        .min(1, "At least one item is required");
    }
    if (isNoteRequired) {
      shape.notes = Yup.string().required("Notes is required");
    }
    return Yup.object().shape(shape);
  }, [isItemRequired, isNoteRequired]);

  const initialValues = useMemo(
    () => ({
      items: isItemRequired ? [{ item: "", id: uuidv4() }] : [],
      notes: "",
    }),
    [isItemRequired]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values) => {
        getFormData(values);
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        setFieldValue,
        errors,
        touched,
      }) => (
        <Form className="h-full flex flex-col justify-between ">
          <div
            style={{
              maxHeight: "290px",
              overflow: "auto",
            }}
          >
            {isItemRequired && (
              <>
                <Heading title="Add Item" customStyle={CUSTOM_lABEL_STYLE} />
                <FieldArray name="items">
                  {() => (
                    <div className="flex flex-col gap-4">
                      {values.items.map((item: any, index: number) => (
                        <div key={item.id}>
                          <div className="w-[100%] flex items-center gap-2">
                            <Input
                              placeholder="Enter Item"
                              name={`items[${index}].item`}
                              value={item.item}
                              onChangeCb={handleChange}
                              size="small"
                            />
                            {index > 0 && (
                              <img
                                src={deleteIcon}
                                className="cursor-pointer"
                                alt="delete-icon"
                                onClick={() => {
                                  const updatedItems = values.items.filter(
                                    (currentItem: any) =>
                                      currentItem.id !== item.id
                                  );
                                  setFieldValue("items", updatedItems);
                                }}
                              />
                            )}
                          </div>
                          <ErrorMessage
                            name={`items[${index}].item`}
                            component="div"
                            className="text-red-600 font-roboto font-normal text-xs mt-1 ml-3"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </FieldArray>
              </>
            )}

            {isNoteRequired && (
              <div className={isItemRequired ? "mt-6" : ""}>
                <Heading
                  title={TEXT_CONSTANTS?.NOTES}
                  customStyle={CUSTOM_lABEL_STYLE}
                />
                <Input
                  multiline={true}
                  rows={2}
                  placeholder={TEXT_CONSTANTS?.NOTES}
                  name={"notes"}
                  value={values["notes"]}
                  onChangeCb={(e: any) => {
                    setFieldValue("notes", e.target.value);
                  }}
                  onBlur={handleBlur}
                  size="small"
                  error={touched["notes"] && Boolean(errors["notes"])}
                  helperText={touched["notes"] ? (errors["notes"] as string) : undefined}
                />
              </div>
            )}
          </div>

          <div className="mt-9 text-end">
            <Button
              type="submit"
              label="Add Order"
              variant="contained"
              style={{
                padding: "8px 55px",
                fontSize: "16px",
              }}
              onClickCb={() => {}}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
}
